import React, { useEffect, useState } from "react";
import Section from "../components/Section";
import { supabase } from "../lib/supabaseClient";

const passkeyEnv = process.env.REACT_APP_ADMIN_PASSKEY;

export default function AdminOrders() {
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("all");
  const [paymentStatus, setPaymentStatus] = useState("all");

  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

  const loadOrders = async () => {
    if (!supabaseUrl || !supabaseKey) {
      setError("Supabase is not configured.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      let query = supabase.from("orders").select("*").order("created_at", { ascending: false }).limit(100);
      if (status !== "all") query = query.eq("status", status);
      if (paymentStatus !== "all") query = query.eq("payment_status", paymentStatus);

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      setOrders(data || []);
    } catch (err) {
      setError(err?.message || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) {
      loadOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, status, paymentStatus]);

  if (!authed) {
    return (
      <Section
        eyebrow="Admin"
        title="Orders"
        description="Enter admin passkey to view latest orders (client-side check only)."
      >
        <div className="max-w-md mx-auto rounded-2xl border border-white/10 bg-white/5 p-6 shadow-xl space-y-3">
          <input
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Passkey"
            className="w-full rounded-xl bg-black/40 border border-white/15 px-3 py-3 text-white focus:border-amber-300 outline-none"
          />
          <button
            onClick={() => {
              if (passkeyEnv && input === passkeyEnv) setAuthed(true);
              else setError("Invalid passkey");
            }}
            className="w-full rounded-full bg-amber-400 text-black font-semibold px-4 py-3 shadow-lg hover:-translate-y-0.5 transition"
          >
            Unlock
          </button>
          {error ? <p className="text-sm text-red-400">{error}</p> : null}
          {!passkeyEnv ? (
            <p className="text-xs text-red-300">
              Set REACT_APP_ADMIN_PASSKEY to enable this page.
            </p>
          ) : null}
        </div>
      </Section>
    );
  }

  return (
    <Section
      eyebrow="Admin"
      title="Recent orders"
      description="Last 100 orders with quick filters."
    >
      <div className="flex flex-wrap gap-3 mb-4">
        <Select
          label="Status"
          value={status}
          onChange={setStatus}
          options={["all", "submitted", "in_progress", "completed", "cancelled"]}
        />
        <Select
          label="Payment"
          value={paymentStatus}
          onChange={setPaymentStatus}
          options={["all", "pending", "paid", "failed"]}
        />
        <button
          onClick={loadOrders}
          className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 text-white px-4 py-2 hover:bg-white/15 transition"
        >
          Refresh
        </button>
      </div>
      {loading ? (
        <div className="text-gray-300">Loading...</div>
      ) : error ? (
        <div className="text-red-400">{error}</div>
      ) : (
        <div className="overflow-auto rounded-2xl border border-white/10 bg-black/40">
          <table className="min-w-full text-sm text-gray-200">
            <thead className="bg-white/5 text-xs uppercase text-gray-300">
              <tr>
                <th className="text-left px-3 py-2">Order #</th>
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Amount</th>
                <th className="text-left px-3 py-2">Payment</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id} className="border-t border-white/5">
                  <td className="px-3 py-2 font-semibold">{order.order_number}</td>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{order.product_name}</div>
                    <div className="text-xs text-gray-400">Qty: {order.quantity}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div>{order.customer_name}</div>
                    <div className="text-xs text-gray-400">{order.customer_phone}</div>
                  </td>
                  <td className="px-3 py-2">₹{order.total_amount_inr}</td>
                  <td className="px-3 py-2">
                    <div className="text-xs uppercase text-gray-300">{order.payment_status}</div>
                    <div className="text-[11px] text-gray-500">{order.payment_link_used}</div>
                  </td>
                  <td className="px-3 py-2 text-xs uppercase text-gray-300">{order.status}</td>
                  <td className="px-3 py-2 text-xs text-gray-400">
                    {order.created_at ? new Date(order.created_at).toLocaleString() : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="flex items-center gap-2 text-sm text-gray-200">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-xl bg-black/40 border border-white/15 px-3 py-2 text-white"
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </label>
  );
}
